import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import type { ChaveDaSecao, PacoteListado } from '../types/baixarProcesso.types';
import { chavesDisponiveis, selecaoEhCompleta } from '../utils/secoesDownload';

export const selectMenuBaixarAberto = (state: RootState) => state.baixarProcesso.menuAberto;
export const selectPainelBaixarAberto = (state: RootState) => state.baixarProcesso.painelAberto;
export const selectFormatoDownload = (state: RootState) => state.baixarProcesso.formato;
export const selectFormatoEmEscolha = (state: RootState) => state.baixarProcesso.formatoEmEscolha;
export const selectSecoesMarcadas = (state: RootState) => state.baixarProcesso.secoesMarcadas;
export const selectEtapaGeracao = (state: RootState) => state.baixarProcesso.etapa;
export const selectPacoteGerado = (state: RootState) => state.baixarProcesso.pacote;
export const selectPacoteEmEspera = (state: RootState) => state.baixarProcesso.pacoteEmEspera;
export const selectErroGeracao = (state: RootState) => state.baixarProcesso.erroGeracao;
export const selectPacoteReaproveitado = (state: RootState) => state.baixarProcesso.reaproveitado;
export const selectOpcoesDosAutos = (state: RootState) => state.baixarProcesso.opcoes;
export const selectCarregandoOpcoes = (state: RootState) => state.baixarProcesso.carregandoOpcoes;
export const selectAvisoDaSelecao = (state: RootState) => state.baixarProcesso.avisoDaSelecao;
export const selectArquivoEmPreparo = (state: RootState) => state.baixarProcesso.arquivoEmPreparo;
export const selectRegistroDeAnexos = (state: RootState) => state.baixarProcesso.registroDeAnexos;
export const selectCarregandoRegistro = (state: RootState) =>
  state.baixarProcesso.carregandoRegistro;
export const selectFalhaNoRegistro = (state: RootState) => state.baixarProcesso.falhaNoRegistro;
export const selectEnviandoCiencia = (state: RootState) => state.baixarProcesso.enviandoCiencia;

/**
 * O documento pedido passa do tamanho que o PDF aceita. Repetir o mesmo pedido falha igual — e o
 * ZIP é o MESMO documento completo, dividido depois de montado —, então só um recorte menor sai.
 */
export const selectFalhouPorLimite = (state: RootState) =>
  state.baixarProcesso.codigoDaFalha === 'LIMITE_EXCEDIDO';

function mesmaSelecao(pacote: PacoteListado, marcadas: readonly ChaveDaSecao[], completa: boolean) {
  if (completa) return pacote.secoes === null;
  if (!pacote.secoes || pacote.secoes.length !== marcadas.length) return false;
  return marcadas.every((chave) => pacote.secoes?.includes(chave));
}

/**
 * O pacote pronto, da versão de AGORA, que já tem exatamente a seleção marcada — é o que faz o
 * painel dizer, antes do clique, que o download sai na hora. Marcar tudo o que está disponível é
 * pedir o documento completo, então a comparação é com o pacote sem recorte.
 */
export const selectPacoteJaGeradoDaSelecao = createSelector(
  [selectOpcoesDosAutos, selectSecoesMarcadas],
  (opcoes, marcadas): PacoteListado | null => {
    if (!opcoes || marcadas.length === 0) return null;
    const completa = selecaoEhCompleta(marcadas, chavesDisponiveis(opcoes.secoes));
    return (
      opcoes.pacotes.find(
        (pacote) =>
          pacote.atual && pacote.status === 'PRONTO' && mesmaSelecao(pacote, marcadas, completa),
      ) ?? null
    );
  },
);
